import { useEffect, useState } from "react"
import { NavLink, Outlet, useLocation } from "react-router-dom"
import AuthPage from "@/pages/AuthPage"
import { useAuth } from "@/hooks/useAuth"
import { useAuthModal } from "@/hooks/useAuthModal"
import { APP_LOGO, APP_NAME } from "@/lib/brand"
import { privacyInbox, privacyNotifications } from "@/lib/privacy"
import { listBlockedIds } from "@/lib/social"
import type { AppNotification, DirectMessage } from "@/types"

const tabs = [
  { to: "/", label: "Home", icon: HomeIcon },
  { to: "/explore", label: "Explore", icon: SearchIcon },
  { to: "/create", label: "Create", icon: PlusIcon, create: true },
  { to: "/wallet", label: "Wallet", icon: WalletIcon },
  { to: "/profile", label: "Profile", icon: UserIcon },
]

export default function AppShell() {
  const location = useLocation()
  const { user } = useAuth()
  const { openAuth, closeAuth } = useAuthModal()
  const [unreadMessages, setUnreadMessages] = useState(0)
  const [unreadNotes, setUnreadNotes] = useState(0)

  useEffect(() => {
    if (location.pathname === "/auth") openAuth()
  }, [location.pathname, openAuth])

  useEffect(() => {
    if (!user?.userId) {
      setUnreadMessages(0)
      setUnreadNotes(0)
      return
    }
    Promise.all([
      privacyInbox(),
      privacyNotifications(),
      listBlockedIds(user.userId),
    ])
      .then(([messagesList, notesList, blocked]) => {
        const blockedSet = new Set(blocked)
        const messages = messagesList.filter((item: DirectMessage) => item.recipientId === user.userId && !blockedSet.has(item.senderId))
        setUnreadMessages(messages.length > 0 ? Math.min(messages.length, 9) : 0)
        const notes = notesList.filter((item: AppNotification) => item.recipientId === user.userId && !item.read && !blockedSet.has(item.actorId))
        setUnreadNotes(notes.length)
      })
      .catch((err) => console.error("Badge load failed:", err))
  }, [user?.userId, location.pathname])

  const hideChrome = location.pathname === "/"

  return (
    <div className="min-h-screen bg-black text-white">
      <header className={`sticky top-0 z-[70] ${hideChrome ? "bg-transparent" : "border-b border-[#1F1F1F] bg-black/95 backdrop-blur-md"}`}>
        <div className="relative mx-auto flex h-20 w-full max-w-6xl items-center px-3 sm:h-24 sm:px-6">
          <NavLink to="/" onClick={closeAuth} className="flex shrink-0 items-center pr-16" aria-label={APP_NAME}>
            <img
              src={APP_LOGO}
              alt={APP_NAME}
              className="h-[4.5rem] w-auto max-h-full max-w-[min(72vw,420px)] object-contain object-left sm:h-20 sm:max-w-[480px] lg:h-24 lg:max-w-[560px]"
            />
          </NavLink>
          <div className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-3 text-white sm:right-6">
            <NavLink to="/notifications" onClick={closeAuth} className="relative" aria-label="Notifications">
              <BellIcon />
              {unreadNotes > 0 ? (
                <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#00FF66] px-1 text-[9px] font-bold text-black">
                  {Math.min(unreadNotes, 9)}
                </span>
              ) : null}
            </NavLink>
            <NavLink to="/messages" onClick={closeAuth} className="relative" aria-label="Messages">
              <MessageIcon />
              {unreadMessages > 0 ? (
                <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#00FF66] px-1 text-[9px] font-bold text-black">
                  {Math.min(unreadMessages, 9)}
                </span>
              ) : null}
            </NavLink>
          </div>
        </div>
      </header>

      <main className={`mx-auto min-h-[calc(100vh-7.5rem)] w-full max-w-6xl bg-black ${hideChrome ? "pb-20" : "pb-24"}`}>
        <Outlet />
      </main>
      <AuthPage />

      <nav className="fixed bottom-0 left-0 right-0 z-[60] border-t border-[#1F1F1F] bg-black/80 backdrop-blur-xl">
        <div className="mx-auto grid w-full max-w-6xl grid-cols-5 pb-[env(safe-area-inset-bottom)]">
          {tabs.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={tab.to === "/"}
              onClick={closeAuth}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 py-3 text-[10px] ${isActive ? "text-[#00FF66]" : "text-[#8A8A8A]"}`
              }
            >
              {tab.create ? (
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#00FF66] text-black">
                  <tab.icon />
                </span>
              ) : (
                <tab.icon />
              )}
              {tab.label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  )
}

function HomeIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-5v-6H10v6H5a1 1 0 0 1-1-1z" />
    </svg>
  )
}

function SearchIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="11" cy="11" r="6.5" />
      <path d="m16 16 4 4" />
    </svg>
  )
}

function PlusIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
      <path d="M12 6v12M6 12h12" />
    </svg>
  )
}

function WalletIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="3.5" y="6" width="17" height="12" rx="2.5" />
      <path d="M16 12.5h3" />
    </svg>
  )
}

function UserIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="12" cy="8" r="3.2" />
      <path d="M5 19c1.4-3 4-4.5 7-4.5s5.6 1.5 7 4.5" />
    </svg>
  )
}

function MessageIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M4.5 6.5h15v11h-6.2L12 20.2 10.7 17.5H4.5z" />
    </svg>
  )
}

function BellIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M6 9a6 6 0 1 1 12 0c0 4 1.5 5.5 1.5 5.5H4.5S6 13 6 9z" />
      <path d="M10 18.5a2 2 0 0 0 4 0" />
    </svg>
  )
}
